import {
  httpUpload,
  slugifyFilename,
  useDisplayUserError
} from '@yes.technology/react-toolkit'
import apiAsync from 'infrastructure/shared/api/api-async'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

type OnFileUploadProps = {
  url: string
  body: File
  contentType: string
  onProgress: (percentage: number) => void
}

type StatusKey =
  | 'awaiting-upload'
  | 'selected-file'
  | 'uploading-file'
  | 'upload-error'
  | 'upload-success'

const useFileUpload = () => {
  const { t } = useTranslation()

  const { displayUserError } = useDisplayUserError()

  const [isLoading, setIsLoading] = useState(false)

  const getPostSignedUploadUrl = useCallback(
    async (params: { filename: string }) => {
      setIsLoading(true)
      return apiAsync.requestSync({
        endpoint: 'signed-upload-url',
        method: 'POST',
        obj: {
          ...params,
          filename: slugifyFilename(params.filename)
        },
        showLoading: false
      })
    },
    []
  )

  const onFileUpload = useCallback(
    async (uploadRequestParams: OnFileUploadProps) => {
      setIsLoading(true)

      try {
        await httpUpload({
          url: uploadRequestParams.url,
          body: uploadRequestParams.body,
          contentType: uploadRequestParams.contentType,
          onProgress: uploadRequestParams.onProgress
        })
      } catch (error) {
        displayUserError({
          error: new Error(t('upload-status.upload-error')),
          title: t('upload-status.upload-error'),
          userMessageFallback: t('upload-status.upload-error')
        })
      } finally {
        setIsLoading(false)
      }
    },
    [t, displayUserError]
  )

  const getStatusMessage = useCallback(
    (key: StatusKey) => {
      return t(`upload-status.${key}`)
    },
    [t]
  )

  return {
    getPostSignedUploadUrl,
    onFileUpload,
    getStatusMessage,
    isLoading,
    setIsLoading
  }
}

export default useFileUpload
